import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { ShopifyOrder } from 'store/orders/ordersStore'

export interface GetShopifyOrdersParams {
  limit?: number;
  offset?: number;
  status?: string;
  fulfillmentStatus?: string;
  financialStatus?: string;
  createdAtMin?: string;
  createdAtMax?: string;
  /**
   * VRG-512: scope the list to one Vera company — the company detail page's Orders
   * tab. Server-side (`GetShopifyOrdersUseCase.scopeToCompany`), not a client filter,
   * so the tab isn't limited to whatever page the standalone list happens to hold.
   */
  companyId?: string;
}

export interface ShopifyOrdersResponse {
  orders: ShopifyOrder[];
  total: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string;
  };
}

// Query keys
export const ordersKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersKeys.all, 'list'] as const,
  list: (filters: GetShopifyOrdersParams) => [...ordersKeys.lists(), filters] as const,
  details: () => [...ordersKeys.all, 'detail'] as const,
  detail: (id: string) => [...ordersKeys.details(), id] as const,
}

// Paginated query for orders
export const useShopifyOrders = (params: GetShopifyOrdersParams = {}) => {
  return useQuery({
    queryKey: ordersKeys.list(params),
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/shop/orders',
        params,
      })
      // `createApiCall`'s errorHandler RESOLVES rather than rejecting: it returns
      // `error.response` on 4xx/5xx, and on a transport failure (API down, DNS, CORS) there is
      // no `.response` at all, so it returns a serialized error whose `.data` is undefined.
      // Returning that undefined made React Query raise its own
      // `["orders","list",{…}] data is undefined` — a red "Error loading orders" banner
      // naming an internal query key, shown for what is usually just "the API is restarting".
      // Check explicitly and throw something a human can read.
      const status = response?.status
      if (typeof status !== 'number') {
        throw new Error('Could not reach the orders service. Check your connection and retry.')
      }
      if (status < 200 || status >= 300) {
        throw new Error(
          (response?.data as any)?.message || `Could not load orders (${status}).`,
        )
      }
      // A successful response with no orders is an EMPTY LIST, never an error — the page
      // should render its empty state, not a banner.
      return (response.data ?? { orders: [], total: 0 }) as ShopifyOrdersResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })
}

// Single-order detail query (VERA-894 order detail page)
export const useOrder = (id?: string) => {
  return useQuery({
    queryKey: ordersKeys.detail(id || ''),
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/orders/${encodeURIComponent(id ?? '')}`,
      })

      return response.data as ShopifyOrder
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })
}

/**
 * VERA-891: download the Vera-branded order document (PDF).
 *
 * Fetched as a blob rather than linked to directly, because the endpoint is
 * session-gated (`internal_order_view`) — a bare <a href> would send no auth
 * header. The object URL is revoked after the browser has had a chance to open
 * it; revoking synchronously cancels the load in some browsers.
 */
export const useOrderDocumentDownload = () => {
  return useMutation({
    mutationFn: async ({ orderId, orderName }: { orderId: string; orderName?: string }) => {
      const response = await createApiCall({
        method: 'GET',
        url: `/shop/orders/${encodeURIComponent(orderId)}/document`,
        responseType: 'blob',
      })

      // The api client does not reject non-2xx, so an error response arrives
      // here as a JSON blob. Without this guard it would be saved as a .pdf —
      // a corrupt file and no error shown.
      const contentType = String(response.headers?.['content-type'] ?? '')
      if (response.status >= 400 || !contentType.includes('application/pdf')) {
        throw new Error(`Order document request failed (${response.status})`)
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Order-${(orderName ?? '').replace(/[^a-zA-Z0-9-_]/g, '') || 'document'}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    },
  })
}

// Mutation for updating order status
export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ orderId, status }: { orderId: string; status: string }) => {
      console.log('🔄 Updating order status:', { orderId, status })
      
      const response = await createApiCall({
        method: 'PATCH',
        url: `/shop/orders/${encodeURIComponent(orderId)}/status`,
        data: { status },
      })
      
      console.log('✅ Order status updated:', response.data)
      return response.data
    },
    onSuccess: (data, variables) => {
      console.log('🔄 Invalidating orders queries after status update')
      // Invalidate and refetch orders to get updated data
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
    },
    onError: (error) => {
      console.error('❌ Error updating order status:', error)
    },
  })
}

// Mutation for linking packages to orders (VRG-138 Phase 2: checkbox-grouping
// / shipment model — pass `lineItemIds` to link the given package(s) to EACH
// of those line items in one call; `lineItemId` is kept for back-compat).
// Omit both for an order-level link.
export const useLinkPackagesToOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderId,
      packageIds,
      lineItemId,
      lineItemIds,
    }: {
      orderId: string
      packageIds: string[]
      lineItemId?: string
      lineItemIds?: string[]
    }) => {
      console.log('🔗 Linking packages to order:', { orderId, packageIds, lineItemId, lineItemIds })

      const response = await createApiCall({
        method: 'POST',
        url: `/shop/orders/${encodeURIComponent(orderId)}/packages`,
        data: {
          packageIds,
          ...(lineItemIds?.length ? { lineItemIds } : lineItemId ? { lineItemId } : {}),
        },
      })

      // The axios interceptor RESOLVES on 4xx (never rejects for a known error
      // response), so a rejected link (e.g. package doesn't belong to the
      // order's company → 400) would otherwise look like success and the modal
      // would close silently. Throw explicitly so callers surface the error.
      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        throw new Error(response?.data?.message || 'Could not assign the package.')
      }

      console.log('✅ Packages linked to order:', response.data)
      return response.data
    },
    onSuccess: (data, variables) => {
      console.log('🔄 Invalidating orders queries after linking packages')
      // Invalidate and refetch orders to get updated data
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) })
    },
    onError: (error) => {
      console.error('❌ Error linking packages to order:', error)
    },
  })
}

// VRG-138 REDESIGN (VERA-891): "Create shipment" — group products (each with a
// package assigned) into one shipment packed in one box-size preset. Single-
// product orders call this with the lone line item + default box.
export const useCreateShipment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderId,
      lineItemIds,
      boxPreset,
    }: {
      orderId: string
      lineItemIds: string[]
      // Omitted for a single-product shipment (ships in the product's own package).
      boxPreset?: 'small' | 'medium' | 'large'
    }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/shop/orders/${encodeURIComponent(orderId)}/shipments`,
        data: { lineItemIds, ...(boxPreset ? { boxPreset } : {}) },
      })

      // Interceptor resolves on 4xx — surface a rejected create (e.g. a product
      // lost its package) instead of a silent no-op.
      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        throw new Error(response?.data?.message || 'Could not create the shipment.')
      }

      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) })
    },
    onError: (error) => {
      console.error('❌ Error creating shipment:', error)
    },
  })
}

// VRG-138 REDESIGN (VERA-891): remove one product from a shipment (it returns
// to the order's "to ship" staging area; the shipment is deleted if it empties).
export const useRemoveShipmentProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderId,
      shipmentId,
      lineItemId,
    }: {
      orderId: string
      shipmentId: string
      lineItemId: string
    }) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/shop/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/products/${encodeURIComponent(lineItemId)}`,
      })

      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        throw new Error(response?.data?.message || 'Could not remove the product from the shipment.')
      }

      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) })
    },
    onError: (error) => {
      console.error('❌ Error removing product from shipment:', error)
    },
  })
}

// VRG-138 REDESIGN (VERA-891): "Mark fulfilled" for one shipment (by id).
// Creates a real Shopify fulfillment (or seeded mock state for draft orders /
// dev — see the API's ShopifyServiceProvider.updateOrderFulfillment).
export const useFulfillShipment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, shipmentId }: { orderId: string; shipmentId: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/shop/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/fulfill`,
        data: {},
      })

      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) })
    },
    onError: (error) => {
      console.error('❌ Error fulfilling shipment:', error)
    },
  })
}

// VRG-138 REDESIGN (VERA-891): "Buy label" for one shipment (by id). MONEY
// ACTION in real mode — always sends `confirm: true` (the caller must show a
// confirmation dialog before invoking this mutation). Weight + box come from
// the shipment row (set at create time). Purchases a real Shopify Shipping
// label, or writes seeded mock state for draft orders / dev — see the API's
// ShopifyServiceProvider.purchaseShippingLabel.
export const useBuyShipmentLabel = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, shipmentId }: { orderId: string; shipmentId: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/shop/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/buy-label`,
        data: { confirm: true },
      })

      return response.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ordersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(variables.orderId) })
    },
    onError: (error) => {
      console.error('❌ Error buying shipment label:', error)
    },
  })
}
